<template>
  <b-modal modal-class="hr-ask-for-introductions-modal" :id="id" v-model="visible" size="lg" lazy @hide="finished">
    <template v-slot:modal-header>
      <empty-slot v-if="$apollo.loading"/>
      <h5 v-else-if="state === 'ERRORED'">An unexpected error occurred</h5>
      <b-container fluid v-else-if="state !== 'ERRORED'">
        <stepper  :states="states" :current-state="state" />
      </b-container>
      <empty-slot v-else/>
    </template>

    <b-container fluid v-if="loading">
      <loading-spinner class="loading-panel"/>
    </b-container>

    <b-container fluid v-else-if="state === 'ERRORED'">
      <b-alert show variant="danger">
        <pre>{{ err }}</pre>
      </b-alert>
    </b-container>

    <b-container fluid v-else-if="state === 'SELECT_EMPLOYEES'">
      <!-- <b-card-body v-if="isDemoSite">
        <b-alert show variant="info">
          <strong>In the demo site</strong>, your account will also receive this referral request.
        </b-alert>
      </b-card-body> -->
      <b-card-body class="select-employees-message">
        <p>Please select the employees who you would like to request a referral from, you'll be able to compose them an email on the next page.</p>
      </b-card-body>
      <template v-if="company && company.contacts && company.contacts && company.contacts.length">

        <b-card-body class="selected-counter">
          <b-row>
            <b-col col md="6">
              <p>Selected <strong>{{totalSelected}}</strong> out of <strong>{{totalSelectable}} employees</strong></p>
            </b-col>
            <b-col col md="6" class="selected-counter-actions">
              <p v-if="totalSelected !== totalSelectable"><a href="#" @click.prevent="selectAllUsers">Select all</a></p>
              <p v-if="totalSelected === totalSelectable"><a href="#" @click.prevent="deselectAllUsers">Deselect all</a></p>
            </b-col>
          </b-row>
        </b-card-body>

        <b-row v-for="contact in company.contacts" :key="contact.id" class="contact-row">
          <b-col col md="4">
            <b-row class="contact-block">
              <div class="contact-details p-2">
                <img :src="`/static/images/avatars/main${contact.avatar || 1}.png`" class="contact-picture"/>
                <template v-if="contact.latestEmployment">
                  <div class="small-black-title" v-if="contact.latestEmployment.title"><b>{{ contact.latestEmployment.title }}</b></div>
                  <div class="small-font secondary-color" v-if="contact.latestEmployment.companyName"><b>{{ contact.latestEmployment.companyName }}</b></div>
                </template>
              </div>
            </b-row>
          </b-col>
          <b-col col md="8">
            <b-row v-if="contact.users && Array.isArray(contact.users) && contact.users.length" class="employee-cards align-middle">
              <b-col col md="6" v-for="user in contact.users" :key="user.id">
                <b-row class="employee-card align-items-center" @click.prevent="() => toggleUserSelect(contact.id, user.id)"
                  :class="{ 'border-primary': isUserSelected(contact.id, user.id), 'border-secondary': !isUserSelected(contact.id, user.id) }">
                    <b-col col md="10">
                      <p class="small-font" v-if="user.department"><b>{{ user.department.name }}</b></p>
                      <p class="small-black-title">{{ user.fullName }}</p>
                      <p class="small-font" v-if="user.title">{{ user.title }}</p>
                      <p v-if="Array.isArray(contact.referrals) && contact.referrals.find(r => r && r.user && r.user.id === user.id)"
                        class="small-font"><b-badge v-b-tooltip.hover
                          :title="contact.referrals.find(r => r && r.user && r.user.id === user.id).created | formatDate('YYYY-MM-DD HH:mm')"
                          variant="primary">Already sent</b-badge>
                      </p>
                    </b-col>
                    <b-col col md="2">
                      <span class="selected-circle float-right"
                        :class="{ 'border-primary': isUserSelected(contact.id, user.id), 'border-secondary': !isUserSelected(contact.id, user.id) }"/>
                    </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-alert show variant="danger">
          <p>An error occurred fetching candidates information</p>
        </b-alert>
      </template>
    </b-container>

    <b-container fluid v-else-if="state === 'ENTER_TEMPLATE'">
      <b-form @submit="() => false">
        <b-form-group label="Message">
          <b-alert v-if="!isDemoSite && isSuperAdmin" show variant="info">
            Super-admin email templates cannot be changed …yet?
          </b-alert>
          <template v-else>
            <markdown-editor :configs="markdownConfig" v-model="email.body"></markdown-editor>
            <b-form-text>You have a number of formatting options, including Markdown</b-form-text>
          </template>
        </b-form-group>
        <b-form-group label="Recipients">
          <b-badge class="emailAddress" variant="secondary" v-for="emailAddress in emailAddresses" :key="emailAddress">{{ emailAddress }}</b-badge>
        </b-form-group>
      </b-form>
    </b-container>

    <b-container v-else-if="state === 'FINISHED'" class="finished">
      <img src="/static/images/illustrations/email-sent.png" class="text-center" center/>
      <h4><b>Congratulations! Your email has been sent</b></h4>
      <p>A unique email has now been sent to each of the employees you selected asking for them to refer their contacts to the job.</p>
    </b-container>

    <template v-slot:modal-footer>
      <empty-slot v-if="$apollo.loading"/>
      <template v-else>
        <b-button v-if="state === 'ENTER_TEMPLATE'" :disabled="blockSendButton" variant="outline-primary" @click="() => state = 'SELECT_EMPLOYEES'">Back</b-button>
        <b-button v-else-if="state !== 'FINISHED'" variant="outline-primary" @click="finished">Cancel</b-button>
        <div style="flex:1"/>
        <b-button v-if="state === 'SELECT_EMPLOYEES'" variant="primary" @click="() => state = 'ENTER_TEMPLATE'" :class="{'disabled': totalSelected === 0}">Continue</b-button>
        <b-button v-else-if="state === 'ENTER_TEMPLATE'" :disabled="blockSendButton" variant="primary" @click="createReferrals">{{ blockSendButton ? 'Sending...' : 'Send' }}</b-button>
        <b-button class="done-button" v-else-if="state === 'FINISHED'" variant="primary" @click="finished">I'm Done</b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Stepper from '@/components/misc/stepper';
import { mapRoot } from '@src/mixins/root';
import { markdownEditor } from 'vue-simplemde';
import { createTemplateString } from '@/lib/templates';

import ReferralsRecruiterCreateQuery from '@/graphql/queries/ReferralsRecruiterCreate.gql';
import ReferralsCreateMutation from '@/graphql/mutations/ReferralsCreate.gql';
import SendManyEmailsMutation from '@/graphql/mutations/SendManyEmails.gql';

const STATES = {
  'SELECT_EMPLOYEES': {
    title: 'Select Employees',
    number: 1
  },
  'ENTER_TEMPLATE': {
    title: 'Send Emails',
    number: 2
  },
  'FINISHED': {
    title: 'Finished',
    number: 3
  },
  // 'CLOSED': {},
};

const DEFAULT_DATA = () => ({
  state: null,
  err: null,
  selectedContacts: {},
  email: {
    subject: null,
    body: null,
  },
  blockSendButton: false,
});

export default {
  name: 'HrAskForIntroductionsModal',
  props: {
    id: {
      type: String,
      default: () => null,
    },
    jobID: {
      type: String,
    },
    selectedContactIDs: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    LoadingSpinner,
    markdownEditor,
    Stepper,
    EmptySlot: {
      functional: true,
      render: (h, { data }) => h('template', data, ''),
    },
  },
  data: () => ({
    visible: false,
    ...DEFAULT_DATA(),
  }),
  metaInfo() {
    if (this.visible) {
      return {
        title: this.currentTitle,
      };
    } else {
      return {};
    }
  },
  apollo: {
    company: {
      query: ReferralsRecruiterCreateQuery,
      variables() {
        return {
          contactIDs: this.selectedContactIDs.map(id => `${id}`),
          jobID: this.jobID,
          templates: [
            // 'EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_SUBJECT',
            'EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_BODY',
          ],
        };
      },
      // update({ company }) {
      //   if (this.isDemoSite) {
      //     if (Array.isArray(company.contacts)) {
      //       company.contacts = company.contacts.map((contact, ci) => {
      //         contact.users = contact.users.filter(({ role }) => role === 'EMPLOYEE');
      //         if (contact.users.length === 0) {
      //           company.contacts.splice(ci, 1);
      //         }
      //
      //         return contact;
      //       });
      //     }
      //   }
      //
      //   return company;
      // },
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company } = data;
          if (company && Array.isArray(company.templates)) {
            company.templates.forEach(({ key, value }) => {
              if (key === 'EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_SUBJECT') {
                this.email.subject = value;
              }
              if (key === 'EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_BODY') {
                this.email.body = value;
              }
            });
          }

          this.selectAllUsers();
        }
      },
      error(err) {
        this.state = 'ERRORED';
        this.err = `${err}`;
      },
      skip() {
        return !this.visible || !this.selectedContactIDs || !this.selectedContactIDs.length;
      },
    },
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'isDemoSite', 'isSuperAdmin' ]),
    currentTitle() {
      const { title } = STATES[this.state] || {};
      return title;
    },
    loading() {
      return this.$apollo.loading;
    },
    states() {
      return STATES;
    },
    emailAddresses() {
      return Object.keys(this.selectedContacts).reduce((emailAddresses, contactID) => {
        if (Array.isArray(this.selectedContacts[contactID]) && this.selectedContacts[contactID].length) {
          this.company.contacts.filter(({ id }) => id === contactID).forEach(({ users }) => {
            users.filter(({ id }) => this.selectedContacts[contactID].includes(id)).forEach(({ emailAddress }) => {
              if (!emailAddresses.includes(emailAddress)) {
                emailAddresses.push(emailAddress);
              }
            });
          });
        }

        return emailAddresses;
      }, []);
    },
    totalSelected() {
      return Object.keys(this.selectedContacts).reduce((count, userID) => count + this.selectedContacts[userID].length, 0);
    },
    totalSelectable() {
      return this.company.contacts.reduce((count, contact) => count + contact.users.length, 0);
    },
    markdownConfig: () => ({
      // autoDownloadFontAwesome: false,
      forceSync: true,
      indentWithTabs: false,
      renderingConfig: { codeSyntaxHighlighting: false },
      promptURLs: true,
      spellChecker: false,
      status: false,
      toolbar: [ 'bold', 'italic', '|', 'link', 'horizontal-rule' ],
    }),
  },
  methods: {
    show() {
      (data => {
        for (const prop in data) {
          this[prop] = data[prop];
        }
      })({
        ...DEFAULT_DATA(),
        // state: 'FINISHED',
        state: 'SELECT_EMPLOYEES',
        visible: true,
      });
    },
    isUserSelected(contactID, userID) {
      return Array.isArray(this.selectedContacts[contactID]) && this.selectedContacts[contactID].includes(userID);
    },
    selectAllUsers() {
      this.selectedContacts = this.company.contacts.reduce((contactIDs, { id: contactID, users }) => {
        contactIDs[contactID] = (users || []).map(({ id: userID }) => userID);
        return contactIDs;
      }, {});
    },
    deselectAllUsers() {
      this.selectedContacts = this.company.contacts.reduce((contactIDs, { id: contactID }) => {
        contactIDs[contactID] = [];
        return contactIDs;
      }, {});
    },
    toggleUserSelect(contactID, userID) {
      if (this.isUserSelected(contactID, userID)) {
        this.selectedContacts[contactID].splice(this.selectedContacts[contactID].indexOf(userID), 1);
      } else {
        this.selectedContacts[contactID] = (this.selectedContacts[contactID] || []).concat([ userID ]);
      }
    },
    async createReferrals() {
      const template = !this.isSuperAdmin && this.email.body ? createTemplateString(this.email.body) : () => null;
      this.blockSendButton = true;

      try {
        const referrals = await (async () => {
          const { job } = this.company;
          const payload = Object.keys(this.selectedContacts).reduce((list, contactID) => {
            if (this.selectedContacts[contactID].length) {
              const contact = this.company.contacts.filter(({ id }) => contactID === id).shift();
              return list.concat(this.selectedContacts[contactID].map(userID => {
                const user = contact.users.filter(({ id }) => userID === id).shift();

                return {
                  user: { id: userID },
                  job: { id: this.jobID },
                  contact: { id: contactID },
                  recruiter: { id: this.user.id },
                  recruiterMessage: template ? template({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    jobTitle: job.title,
                  }) : null,
                  status: 'REQUESTED',
                };
              }));
            } else {
              return list;
            }
          }, []);

          if (!payload.length) {
            throw new Error('No users were selected');
          }

          const { data, errors } = await this.$apollo.mutate({
            mutation: ReferralsCreateMutation,
            variables: { create: payload },
          });

          if (errors) {
            errors.map(err => this.$log.error(err));
          }

          if (!data || !Array.isArray(data.createManyReferrals)) {
            throw new Error('An unknown error occurred');
          }

          // console.log(data.createManyReferrals);
          return data.createManyReferrals;
        })();

        await (async () => {
          // @TODO Move this email-sending logic to the backend

          if (this.isDemoSite) {
            // No need to send emails in the demo site
            // So exit this flow early!
            return true;
          }

          const emails = (() => {
            const results = {};

            referrals.forEach(referral => {
              if (referral && referral.id && referral.user && referral.user.id) {
                if (!results.hasOwnProperty(referral.user.id)) {
                  results[referral.user.id] = {
                    // template: this.isSuperAdmin ? 'referrals/employee-request-single' : 'referrals/employee-request',
                    template: 'referrals/employee-request',
                    where: {
                      users: {
                        id: referral.user.id,
                        status: 'ACTIVE',
                      },
                    },
                    // data: {
                    //   subject: this.email.subject || undefined,
                    // },
                    variables: {
                      jobID: this.jobID,
                      recruiterID: this.user.id,
                      referralIDs: [],
                    },
                  };
                }

                results[referral.user.id].variables.referralIDs.push(referral.id);

                // if (this.isSuperAdmin) {
                //   if (results[referral.user.id].variables.referralIDs.length > 1) {
                //     results[referral.user.id].template = 'referrals/employee-request-multiple';
                //   }
                //
                //   this.$root.captureLog({
                //     eventName: 'SUPER_ADMIN_REFERRAL_REQUESTED',
                //     user: { id: referral.user.id },
                //     data: [ {
                //       key: 'referralID',
                //       value: referral.id,
                //     } ]
                //   });
                // }
              }
            });

            return Object.values(results).map(payload => {
              payload.data = JSON.stringify(payload.data);
              payload.variables = JSON.stringify(payload.variables);
              return payload;
            });
          })();

          const { data, errors } = await this.$apollo.mutate({
            mutation: SendManyEmailsMutation,
            variables: { emails },
          });

          if (errors) {
            errors.map(err => this.$log.error(err));
          }

          if (!data || !data.sendManyEmails) {
            this.$log.error(new Error('Failed to send an email to users'));
          }
        })();

        this.state = 'FINISHED';
        this.$emit('clearBasket');
      } catch (err) {
        this.state = 'ERRORED';
        this.err = `${err}`;
      }
    },
    finished() {
      this.visible = false;

      if (this.state === 'FINISHED') {
        this.$emit('clearBasket');
      }
    },
  },
};
</script>

<style lang="scss">
@import './../scss/variables';
.hr-ask-for-introductions-modal {

  @media (min-width: 992px) {
    .modal-lg {
      max-width: 900px;
    }
  }

  .modal-header {
    border: 0;
    padding-bottom: 0;
  }
  .modal-body {
    padding-top: 0;
    .card-body {
      padding-top: 0;
      padding-bottom: 0;
    }
    .select-employees-message {
      padding-top: 1rem;
      padding-bottom: 1rem;
      text-align: center;
    }
  }

  .modal-footer {
    position: relative;
    .btn {
      width: 10em;
    }
  }

  .badge.emailAddress {
    margin-right: 0.2rem;
    background-color: #C6C9CC;
    font-size: 85%;
    font-weight: normal;
  }
  .finished {
    h4 {
      margin-top: 2em;
    }
    text-align: center;
    max-width: 45em;
    img {
      margin: 0 auto;
      width: 20em;
      display:block;
    }
  }
  .contact-row {
    margin-bottom: 2em;
    margin-left: 0.75rem;
    margin-right: 0.75rem;
    .contact-block {
      height: 100%;
      background-color: #fbfbfd;
      // TODO add min height and vertical middle for employee cards
      // min-height: 12rem;
      .contact-details {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 100%;
        transform: translate(-50%, -50%);
        padding-top:0.5em;
        img {
          display: block;
          margin-left: auto;
          margin-right: auto;
          margin-bottom: 0.4em;
        }
        .small-font, .small-black-title {
          padding-top: 0.2em;
          text-align: center;
        }
      }
    }
  }

  .selected-counter {
    p {
      font-size: $small-font-size;
    }

    .selected-counter-actions {
      text-align: right;
    }
  }

  .employee-cards {
    .contact-picture {
      margin: 0 auto;
    }
    table {
      height: 100%;
      width: 100%;
    }

    .employee-card {
      border-style: solid;
      border-width: 1px;
      border-radius: 4px;
      padding: 1em;
      margin-bottom: 1rem;
      height: 8rem;
      .selected-circle {
        border-width: 3px;
        margin-right: -0.5rem;
      }
      @media (max-width: 1080px) {
        height: 20rem;
      }
      .small-font, .small-black-title {
        margin-bottom: 0.25em;
        margin-top: 0.25em;
        line-height: 1.2em;
      }
    }
    > :nth-last-child(2), > :nth-last-child(1) {
      .employee-card {
        margin-bottom: 0px;
      }
    }
    > :nth-last-child(odd) {
      .employee-card {
        margin-left: 0.5em;
      }
    }
    > :nth-last-child(even) {
      .employee-card {
        margin-left: 0.5em;
      }
    }
  }

  .CodeMirror {
    .CodeMirror-lines {
      padding-bottom: 20px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modals/hr-ask-for-introductions.vue